import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { useEffect } from "react";

import copy from "../../public/locales/en/analytics.json";
import { useAppDispatch, useAppSelector } from "../store";
import AnalyticsRowsTable from "./AnalyticsRowsTable";
import {
  fetchLandingFunnel,
  setFunnelFilters,
} from "../store/slices/productAnalyticsSlice";
import { analyticsRatio, countryName } from "../utils/analyticsFormatting";
import {
  filterFunnelRows,
  sumFunnelField,
} from "../utils/landingFunnelFormatting";

import type { ReactElement } from "react";

const labels = copy.funnel;
export default function LandingFunnelReport(): ReactElement {
  const dispatch = useAppDispatch();
  const {
    days,
    funnel: report,
    funnelError: error,
    funnelFilters: filters,
  } = useAppSelector((state) => state.productAnalytics);
  useEffect((): (() => void) => {
    const request = dispatch(fetchLandingFunnel(days));
    return (): void => request.abort();
  }, [dispatch, days]);
  const rows = filterFunnelRows(report?.rows ?? [], filters);
  const visits = sumFunnelField(rows, "visits");
  const fields = [
    "visits",
    "views",
    "shadowingOpened",
    "dictationOpened",
    "shadowingStarted",
    "dictationStarted",
    "practiced",
    "registered",
  ] as const;
  const filterNames = [
    "source",
    "campaign",
    "content",
    "device",
    "country",
  ] as const;
  return (
    <Paper component="section" sx={{ p: { xs: 2, md: 3 }, mt: 3 }}>
      <Typography component="h2" variant="h5">
        {labels.title}
      </Typography>
      <Typography sx={{ my: 2 }} color="text.secondary">
        {labels.note}
      </Typography>
      {error && <Alert severity="error">{labels.error}</Alert>}
      {!report && !error && <CircularProgress aria-label={labels.loading} />}
      {report && (
        <>
          {!report.enabled && <Alert severity="info">{labels.disabled}</Alert>}
          <Typography variant="body2" sx={{ my: 2 }}>
            {labels.retention}
          </Typography>
          {report.firstRecordedAt && (
            <Typography variant="body2">
              {labels.firstRecorded}: {report.firstRecordedAt}
            </Typography>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, my: 2 }}>
            {filterNames.map((name): ReactElement => {
              const values =
                name === "device"
                  ? ["mobile", "desktop", "tablet", "unknown"]
                  : Array.from(
                      new Set(
                        report.rows.map((row): string =>
                          name === "source"
                            ? (row.source ?? row.referrerHost ?? "")
                            : (row[name] ?? ""),
                        ),
                      ),
                    ).sort();
              if (filters[name] !== "all" && !values.includes(filters[name]))
                values.push(filters[name]);
              return (
                <FormControl key={name} size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id={`funnel-${name}`}>{labels[name]}</InputLabel>
                  <Select
                    labelId={`funnel-${name}`}
                    label={labels[name]}
                    value={filters[name]}
                    onChange={(event): void => {
                      dispatch(
                        setFunnelFilters({ [name]: event.target.value }),
                      );
                    }}
                  >
                    <MenuItem value="all">{labels.all}</MenuItem>
                    {values.map(
                      (value): ReactElement => (
                        <MenuItem key={value} value={value}>
                          {name === "country"
                            ? countryName(value || null)
                            : name === "device"
                              ? labels.devices[
                                  value as keyof typeof labels.devices
                                ]
                              : value || copy.campaign.unknown}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
              );
            })}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.excludeAutomated}
                onChange={(_event, checked): void => {
                  dispatch(setFunnelFilters({ excludeAutomated: checked }));
                }}
              />
            }
            label={labels.excludeAutomated}
          />
          <Typography variant="body2" color="text.secondary">
            {labels.trafficNote}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              gap: 2,
              my: 3,
            }}
          >
            {fields.map(
              (field): ReactElement => (
                <Box
                  key={field}
                  sx={{ bgcolor: "grey.50", p: 2, borderRadius: 2 }}
                >
                  <Typography>{labels[field]}</Typography>
                  <Typography variant="h5">
                    {!report.enabled && !report.firstRecordedAt
                      ? copy.campaign.noData
                      : sumFunnelField(rows, field)}
                  </Typography>
                </Box>
              ),
            )}
          </Box>
          <Typography>
            {labels.practiceRate}:{" "}
            <strong>
              {analyticsRatio(
                visits ? sumFunnelField(rows, "practiced") / visits : null,
              )}
            </strong>
          </Typography>
          <Typography>
            {labels.registrationRate}:{" "}
            <strong>
              {analyticsRatio(
                visits ? sumFunnelField(rows, "registered") / visits : null,
              )}
            </strong>
          </Typography>
          {rows.length === 0 ? (
            <Typography sx={{ my: 2 }}>{labels.empty}</Typography>
          ) : (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary>{labels.details}</AccordionSummary>
              <AccordionDetails>
                <AnalyticsRowsTable
                  rows={rows}
                  label={labels.details}
                  columns={[
                    {
                      label: labels.source,
                      value: (row): string =>
                        row.source ?? row.referrerHost ?? copy.campaign.unknown,
                    },
                    {
                      label: labels.campaign,
                      value: (row): string => row.campaign ?? "—",
                    },
                    {
                      label: labels.content,
                      value: (row): string => row.content ?? "—",
                    },
                    {
                      label: copy.campaign.term,
                      value: (row): string => row.term ?? "—",
                    },
                    {
                      label: labels.country,
                      value: (row): string => countryName(row.country),
                    },
                    {
                      label: labels.locale,
                      value: (row): string =>
                        row.locale ?? copy.campaign.unknown,
                    },
                    {
                      label: labels.device,
                      value: (row): string => labels.devices[row.device],
                    },
                    {
                      label: labels.traffic,
                      value: (row): string => labels.trafficKinds[row.traffic],
                    },
                    {
                      label: labels.reason,
                      value: (row): string =>
                        row.reason
                          ? (labels.reasons[
                              row.reason as keyof typeof labels.reasons
                            ] ?? row.reason)
                          : "—",
                    },
                    ...fields.map((field) => ({
                      label: labels[field],
                      value: (row: (typeof rows)[number]): number => row[field],
                    })),
                  ]}
                />
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </Paper>
  );
}
